<template>
  <div
    class="product-card-image-slider"
    :class="{ 'product-card-image-slider--interacted': isInteracted }"
    @mouseenter="showPagination"
    @mouseleave="hidePagination"
    @focusin="showPagination"
    @focusout="hidePagination"
    @mousedown="handleSwipeStart"
    @mousemove="handleSwipeMove"
    @mouseup="handleSwipeEnd"
    @touchstart.passive="handleSwipeStart"
    @touchmove.passive="handleSwipeMove"
    @touchend.passive="handleSwipeEnd"
    @touchcancel.passive="handleSwipeEnd"
    @click.capture="preventLinkAfterSwipe"
  >
    <Swiper
      ref="swiper"
      :options="swiperOptions"
      @slideChange="updateActiveIndex"
    >
      <SwiperSlide
        v-for="(image, index) in images"
        :key="`${image.path}-${index}`"
      >
        <img
          class="product-card-image-slider__image swiper-lazy"
          :data-src="image.path"
          alt=""
          draggable="false"
        >
        <div class="swiper-lazy-preloader" />
      </SwiperSlide>
    </Swiper>

    <div
      v-if="images.length > 1"
      class="product-card-image-slider__pagination"
      :class="{ 'product-card-image-slider__pagination--centered': isPaginationCentered }"
    >
      <span
        v-for="item in visiblePaginationItems"
        :key="item.index"
        class="product-card-image-slider__pagination-item"
        :class="{ 'product-card-image-slider__pagination-item--active': item.isActive }"
        role="button"
        :tabindex="isInteracted ? 0 : -1"
        :aria-label="`Slide ${item.index + 1}`"
        @click.prevent.stop="goToSlide(item.index)"
        @keydown.enter.prevent.stop="goToSlide(item.index)"
        @keydown.space.prevent.stop="goToSlide(item.index)"
      />
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'

export default {
  name: 'ProductCardImageSlider',
  components: {
    Swiper,
    SwiperSlide
  },
  props: {
    images: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      activeIndex: 0,
      isInteracted: false,
      paginationHideTimeout: null,
      swipeStartPoint: null,
      isSwipeMoved: false
    }
  },
  watch: {
    images () {
      this.activeIndex = 0
    }
  },
  beforeDestroy () {
    this.clearPaginationHideTimeout()
  },
  computed: {
    paginationStartIndex () {
      if (this.images.length <= 5) return 0

      return Math.min(Math.max(this.activeIndex - 2, 0), this.images.length - 5)
    },
    isPaginationCentered () {
      return this.images.length > 5
    },
    visiblePaginationItems () {
      const length = Math.min(this.images.length, 5)

      return Array.from({ length }, (_, index) => {
        const imageIndex = this.paginationStartIndex + index

        return {
          index: imageIndex,
          isActive: imageIndex === this.activeIndex
        }
      })
    },
    swiperOptions () {
      return {
        slidesPerView: 1,
        loop: false,
        preloadImages: false,
        lazy: {
          loadPrevNext: false
        },
        allowTouchMove: true,
        simulateTouch: true,
        nested: true,
        touchMoveStopPropagation: true,
        navigation: false,
        pagination: false
      }
    }
  },
  methods: {
    getSwiper () {
      if (!this.$refs.swiper) return null

      return this.$refs.swiper.$swiper || this.$refs.swiper.swiperInstance
    },
    updateActiveIndex () {
      const swiper = this.getSwiper()

      if (!swiper) return

      this.activeIndex = swiper.realIndex || 0
    },
    goToSlide (index) {
      const swiper = this.getSwiper()

      if (!swiper) return

      this.showPagination()
      swiper.slideTo(index)
    },
    getEventPoint (event) {
      const touch = event.touches && event.touches[0]
      const changedTouch = event.changedTouches && event.changedTouches[0]
      const point = touch || changedTouch || event

      return {
        x: point.clientX,
        y: point.clientY
      }
    },
    handleSwipeStart (event) {
      this.showPagination()
      this.isSwipeMoved = false
      this.swipeStartPoint = this.getEventPoint(event)
    },
    handleSwipeMove (event) {
      if (!this.swipeStartPoint) return

      const point = this.getEventPoint(event)
      const diffX = Math.abs(point.x - this.swipeStartPoint.x)
      const diffY = Math.abs(point.y - this.swipeStartPoint.y)

      if (diffX > 6 || diffY > 6) {
        this.isSwipeMoved = true
      }
    },
    handleSwipeEnd (event) {
      if (event.type.indexOf('touch') === 0) {
        this.scheduleHidePagination()
      }

      this.swipeStartPoint = null
    },
    preventLinkAfterSwipe (event) {
      if (!this.isSwipeMoved) return

      event.preventDefault()
      event.stopPropagation()
      this.isSwipeMoved = false
    },
    showPagination () {
      this.clearPaginationHideTimeout()
      this.isInteracted = true
    },
    scheduleHidePagination () {
      this.clearPaginationHideTimeout()
      this.paginationHideTimeout = setTimeout(this.hidePagination, 1200)
    },
    hidePagination () {
      this.clearPaginationHideTimeout()
      this.isInteracted = false
    },
    clearPaginationHideTimeout () {
      if (!this.paginationHideTimeout) return

      clearTimeout(this.paginationHideTimeout)
      this.paginationHideTimeout = null
    }
  }
}
</script>

<style lang="scss" scoped>
.product-card-image-slider {
  position: relative;
  width: 200px;

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;

    &.swiper-lazy {
      opacity: 0;
      transition: .3s ease-in-out;
    }

    &.swiper-lazy-loaded {
      opacity: 1;
    }
  }

  &__pagination {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 5;
    justify-content: center;
    gap: 8px;
    margin-top: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity .15s ease-in-out;

    &-item {
      width: 5px;
      height: 5px;
      flex: 0 0 5px;
      margin: 0;
      border-radius: 50%;
      background: #e7e7e7;
      cursor: pointer;
      opacity: 1;
      pointer-events: auto;

      &--active {
        background: #ff2f00;
      }
    }

    &--centered {
      // .product-card-image-slider__pagination-item:first-child,
      // .product-card-image-slider__pagination-item:last-child {
      //   width: 8px;
      //   flex: 0 0 8px;
      //   border-radius: 2px;
      // }
    }
  }

  &--interacted &__pagination,
  &:hover &__pagination,
  &:focus-within &__pagination,
  &:active &__pagination {
    opacity: 1;
  }

  .swiper-lazy-preloader {
    border-width: 2px;
    border-bottom-color: #ff2f00;
    border-left-color: #ff2f00;
    border-right-color: #ff2f00;
  }
}
</style>
